import { stringify } from "query-string";
import React from "react";

import { bazaarGridUrl } from "@/mk2/apps/bazaar/urls";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { StrollerEntity } from "@/mk2/apps/strollers/schemas";
import { strollersAddReviewUrl, strollersDetailUrl } from "@/mk2/apps/strollers/urls";
import { fromB64 } from "@/mk2/apps/users/utils";
import { getWikiArticleUrl, wikiAddExperienceUrl } from "@/mk2/apps/wiki/urls";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { ProductBox } from "@/mk2/components/ProductBox";
import { getBestVersionForThumb } from "@/mk2/decorators/withUploader";
import {
    getAllDescendantsOfAType,
    getAttributesDict,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { findCoverPhotoEntityFromAst } from "@/mk2/helpers/structuredData";
import { url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { AST, ASTLinkedObjectType, ProductBoxWidgetData, WikiArticleEntity } from "@/mk2/schemas";
import { dict } from "@/mk/bazaar/product/categories";
import {
    makeFullFilter,
    serializeFilterForPathname,
    serializeFilterForQuery,
} from "@/mk/bazaar/product/filter/filterUtils";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ProductBoxWidget.mscss";

const logger = getLogger("components/ProductBoxWidget");

interface OwnProps {
    contentAST: AST;
    wikiArticles: WikiArticleEntity[];
    strollers: StrollerEntity[];
    data: Record<string, ProductBoxWidgetData>;
}

type Props = OwnProps;

const ProductBoxWidget: React.FunctionComponent<Props> = ({
    contentAST,
    strollers,
    wikiArticles,
    data,
}) => {
    const attributes = getAttributesDict(contentAST);
    const linkToObjectNode = getAllDescendantsOfAType(contentAST, "link_to_object")[0];

    if (!linkToObjectNode) {
        logger.warn("ProductBoxWidget: no link_to_object node found in AST");
        return null;
    }

    if (linkToObjectNode.obj_type === ASTLinkedObjectType.WIKI_ARTICLE) {
        const article = (wikiArticles ?? []).find((a) => a?.id === linkToObjectNode.obj_id);
        // `category` and `photos` are nested entity refs that denormalize to
        // undefined when dangling (deleted/unloaded) — bail out cleanly since
        // both are dereferenced below (article.category.slug, article.photos).
        if (!article || !article.category) {
            return null;
        }

        const photos = article.photos ?? [];
        const photo =
            article.coverPhoto ||
            findCoverPhotoEntityFromAst(article.bodyAST, photos) ||
            (photos.length > 0 ? photos[0] : null);

        const encodedFilter = attributes.market ? removeQuotes(attributes.market) : null;
        const filter = encodedFilter ? makeFullFilter(JSON.parse(fromB64(encodedFilter))) : null;
        if (filter) {
            // Force display all qualities and set sorting
            filter.settings.quality = ["new", "old"];
            filter.settings.gridSort = "update";
        }
        const marketData = filter ? data?.[`f:${encodedFilter}`] : null;
        const cat = filter?.cats?.length ? dict()[filter.cats[0]] : null;

        return (
            <ProductBox
                className={styles.ProductBoxWidget}
                title={
                    <Link to={getWikiArticleUrl(article.category.slug, article.slug)}>
                        {article.title}
                    </Link>
                }
                image={
                    photo && (
                        <Link to={getWikiArticleUrl(article.category.slug, article.slug)}>
                            <ImgCropped
                                width={64}
                                height={64}
                                mode={ImgCropMode.Embed}
                                imgUrl={photoUrl(photo, getBestVersionForThumb(photo))}
                                imgWidth={photo.width}
                                imgHeight={photo.height}
                                lazy
                            />
                        </Link>
                    )
                }
                reviewsCount={article.experiences.length}
                score={article.experiencesScore}
                productsCount={
                    (marketData?.oldHits ?? 0) +
                    (marketData?.newHits ?? 0) +
                    (marketData?.eshopHits ?? 0)
                }
                productsTo={
                    filter
                        ? {
                              pathname: url(bazaarGridUrl, {
                                  category: cat && cat.slug,
                                  filters: serializeFilterForPathname(filter),
                              }),
                              search: stringify(serializeFilterForQuery(filter)),
                          }
                        : null
                }
                oldProductsMinPrice={marketData?.oldMinPrice}
                // oldProductsMaxPrice={marketData?.oldMaxPrice}
                newProductsMinPrice={marketData?.newMinPrice}
                newProductsMaxPrice={
                    marketData?.newMaxPrice === marketData?.newMinPrice
                        ? null
                        : marketData?.newMaxPrice
                }
                eshopProductsMinPrice={marketData?.eshopMinPrice}
                eshopProductsMaxPrice={marketData?.eshopMaxPrice}
                addReviewTo={url(wikiAddExperienceUrl, {
                    articleSlug: article.slug,
                    categorySlug: article.category.slug,
                })}
            />
        );
    } else if (linkToObjectNode.obj_type === ASTLinkedObjectType.STROLLERS_STROLLER_DETAIL) {
        const stroller = (strollers ?? []).find((s) => s?.id === linkToObjectNode.obj_id);
        if (!stroller) {
            return null;
        }
        const photo = stroller.previewPhoto || dummyPhoto;
        return (
            <ProductBox
                className={styles.ProductBoxWidget}
                title={
                    <Link to={url(strollersDetailUrl, { slug: stroller.slug })}>
                        {stroller.name}
                    </Link>
                }
                image={
                    <Link to={url(strollersDetailUrl, { slug: stroller.slug })}>
                        <ImgCropped
                            width={64}
                            height={64}
                            mode={ImgCropMode.Embed}
                            imgUrl={photo.url100x100}
                            imgWidth={photo.originalWidth}
                            imgHeight={photo.originalHeight}
                            lazy
                        />
                    </Link>
                }
                strollerSlug={stroller.slug}
                reviewsCount={stroller.reviewsCount}
                score={stroller.userScore}
                productsCount={0}
                productsTo={url(strollersDetailUrl, {
                    slug: stroller.slug,
                })}
                oldProductsMinPrice={stroller.priceUsedMin}
                // oldProductsMaxPrice={marketData?.oldMaxPrice}
                eshopProductsMinPrice={stroller.minPricedEshopProduct}
                eshopProductsMaxPrice={stroller.maxPricedEshopProduct}
                addReviewTo={url(strollersAddReviewUrl, { slug: stroller.slug })}
            />
        );
    } else {
        return null;
    }
};

ProductBoxWidget.displayName = "ProductBoxWidget";

export default React.memo<Props>(ProductBoxWidget);
